import { INSTALLATION_APPOINTMENT_TIME_SLOT_ID } from '../../../common/constants/ProductOfferingConstants.js';
import {
  MAIN_USE_OF_SUBSCRIPTION,
  OfferSubCategory,
  RelatedPartyRoles,
  ShipmentOrderEnum,
} from '../../../common/enums.js';
import {
  filterExtensionsWithValues,
  getLocalizedString,
  isSoftwareProduct,
} from '../../../common/utils/productOfferingsUtils.js';
import { DeliveryOption } from '../../../generated/api/deliveryOption.js';
import { getProductConfigDescriptor } from '../productConfig/productConfigRegistryCoCo.js';
import {
  BundleAddonGroupRuntimeCharacteristic,
  DeliveryRuntimeCharacteristic,
  DeviceRuntimeCharacteristic,
  OrderParamExtensions,
  OwnSimCardValue,
  PlanProductRuntimeCharacteristic,
  isPhysicalSimOffering,
} from './characteristicsFlagsUtilsCoCo.js';
import { EnrollmentProgramValue } from './deviceEnrollmentProgramUtilsCoCo.js';
import { normalizeMacAddress } from './macAddressUtilsCoCo.js';
import { isProductSubcategory } from './productSubcategoryUtilsCoCo.js';
import { getDefaultCharacteristicValue, getSelectedCharacteristicValue } from './runtimeCharacteristicsCoCo.js';
import '../productConfig/productConfigSetupCoCo.js';
import type { ProductOfferingCartItems, ShoppingBasketType } from '../../../common/types/shoppingBasketCoCo.js';
import type { CartBundleAddonGroup } from '../../../generated/api/cartBundleAddonGroup.js';
import type { CartProductOfferingCoco } from '../../../generated/api/cartProductOfferingCoco.js';
import type { Contact } from '../../../generated/api/contact.js';
import type { Extensions } from '../../../generated/api/extensions.js';
import type { LocalizedField } from '../../../generated/api/localizedField.js';
import type { ProductOffering } from '../../../generated/api/productOffering.js';
import type { ProductOfferingCoco } from '../../../generated/api/productOfferingCoco.js';
import type { ProductOfferingsResponse } from '../../../generated/api/productOfferingsResponse.js';
import type { ProductOrderDeliveryMethodRequest } from '../../../generated/api/productOrderDeliveryMethodRequest.js';
import type { ProductOrderRequest } from '../../../generated/api/productOrderRequest.js';
import type { ProductSpecRuntimeCharacteristic } from '../../../generated/api/productSpecRuntimeCharacteristic.js';
import type { UpdateProductOrderRequest } from '../../../generated/api/updateProductOrderRequest.js';
import type { CustomerPrimaryAddress } from '../placeOrderTypes.js';
import type { PlaceOrderItemUnitCoCo } from '../placeOrderTypesCoCo.js';

export const COMMERCIAL_RELATION_TYPE = 'commercialRelation';

const NOT_TO_DIRECTORY_VALUE = 'Not to directory';

const isMobileBroadbandOffering = (subcategory?: string): boolean =>
  isProductSubcategory(subcategory, OfferSubCategory.MOBILE_BROADBAND);

const toNameField = (name: string | LocalizedField): LocalizedField =>
  typeof name === 'string' ? { value: name } : { value: name.value ?? '' };

const pickCartProductOffering = (offering: CartProductOfferingCoco) => ({
  id: offering.id,
  name: toNameField(offering.name),
  cartItemId: offering.cartItemId,
  productOfferingType: offering.productOfferingType,
  productOfferingPrice: offering.productOfferingPrice,
  extensions: filterExtensionsWithValues(offering.extensions),
  productOfferingCharacteristic: offering.productOfferingCharacteristic,
  productOfferingRuntimeCharacteristic: offering.productOfferingRuntimeCharacteristic,
  productSpecRuntimeCharacteristics: offering.productSpecRuntimeCharacteristics,
});

const mapProductOfferingExtensions = (
  basketItem: ProductOfferingCartItems,
  installationTimeSlotId?: string
): Extensions[] | undefined => {
  const extensions = basketItem.extensions
    ?.filter(extension => extension.name !== INSTALLATION_APPOINTMENT_TIME_SLOT_ID || installationTimeSlotId)
    .map(extension =>
      extension.name === INSTALLATION_APPOINTMENT_TIME_SLOT_ID
        ? { ...extension, value: [{ value: installationTimeSlotId! }] }
        : extension
    );

  return filterExtensionsWithValues(extensions);
};

const mergeProductOfferingExtensions = (
  basketItem: ProductOfferingCartItems,
  mappedExtensions: Extensions[] | undefined,
  installationTimeSlotId?: string,
  catalogExtensions?: Extensions[]
): Extensions[] | undefined => {
  const extensionsByName = new Map(
    (mapProductOfferingExtensions(basketItem, installationTimeSlotId) ?? []).map(extension => [
      extension.name,
      extension,
    ])
  );

  if (installationTimeSlotId && !extensionsByName.has(INSTALLATION_APPOINTMENT_TIME_SLOT_ID)) {
    const installationSlotExtension = catalogExtensions?.find(
      extension => extension.name === INSTALLATION_APPOINTMENT_TIME_SLOT_ID
    );
    if (installationSlotExtension) {
      extensionsByName.set(INSTALLATION_APPOINTMENT_TIME_SLOT_ID, {
        ...installationSlotExtension,
        value: [{ value: installationTimeSlotId }],
      });
    } else {
      extensionsByName.set(INSTALLATION_APPOINTMENT_TIME_SLOT_ID, {
        name: INSTALLATION_APPOINTMENT_TIME_SLOT_ID,
        target: 'onlineApi/reserveAppointment',
        type: 'String',
        value: [{ value: installationTimeSlotId }],
      });
    }
  }

  filterExtensionsWithValues(mappedExtensions)?.forEach(extension => extensionsByName.set(extension.name, extension));
  const extensions = [...extensionsByName.values()];

  return extensions.length > 0 ? extensions : undefined;
};

const pickCommercialAddonProductOffering = (offering: CartProductOfferingCoco): CartProductOfferingCoco => ({
  ...pickCartProductOffering(offering),
  productOfferingRelationship: offering.productOfferingRelationship,
});

const pickPlanProductOfferingForDraft = (offering: CartProductOfferingCoco): CartProductOfferingCoco => {
  const { productSpecRuntimeCharacteristics: _unresolvedCatalogCharacteristics, ...plan } =
    pickCartProductOffering(offering);
  return plan;
};

const mapBundleAddonGroups = (groups: CartBundleAddonGroup[]) =>
  groups?.map(group => ({
    id: group.id,
    name: toNameField(group.name),
    productOffering: group.productOffering?.filter(o => o.isSelected).map(pickCartProductOffering),
  }));

const mapCartItemToProductOffering = (basketItem: ProductOfferingCartItems): ProductOffering => ({
  id: basketItem.id,
  name: toNameField(basketItem.name),
  cartItemId: basketItem.cartItemId,
  productOfferingType: basketItem.productOfferingType,
  productOfferingPrice: basketItem.productOfferingPrice,
  extensions: mapProductOfferingExtensions(basketItem),
  planProductOffering: basketItem.planProductOffering
    ? pickPlanProductOfferingForDraft(basketItem.planProductOffering)
    : undefined,
  bundleAddonProductOffering: [...(basketItem.bundledAddonProductOffering ?? []).filter(a => a.isChecked)].map(
    pickCartProductOffering
  ),
  commercialAddonProductOffering: [
    ...(basketItem.commercialAddonProductOffering ?? [])
      .filter(a => a.isChecked)
      .map(pickCommercialAddonProductOffering),
  ],
  bundleAddonGroup: mapBundleAddonGroups(basketItem.bundleAddonGroup ?? []),
});

export const SOFTWARE_PRODUCT_CHAR_NAMES = [
  PlanProductRuntimeCharacteristic.USER_FIRST_NAME,
  PlanProductRuntimeCharacteristic.USER_LAST_NAME,
  PlanProductRuntimeCharacteristic.EMAIL_ID,
  PlanProductRuntimeCharacteristic.ORDERING_MOBILE_NUMBER,
  PlanProductRuntimeCharacteristic.LANGUAGE,
];

export const resolveUserContact = (unit: PlaceOrderItemUnitCoCo, contacts: Contact[]): Contact | undefined =>
  contacts.find(c => c.contactId === unit.userContact);

const isCustomDirectoryListing = (value?: unknown): boolean =>
  typeof value === 'string' && value.toUpperCase() === 'CUSTOM';

const getDirectoryListingPublicityValue = (unit: PlaceOrderItemUnitCoCo): string | undefined => {
  if (!unit.directoryListingPublicity) {
    return undefined;
  }

  if (!isCustomDirectoryListing(unit.directoryListingPublicity)) {
    return unit.directoryListingPublicity;
  }

  const details = unit.directoryListingCustomPublicityDetails;
  const parts = [
    details?.companyName,
    details?.recipientName,
    details?.address?.line,
    details?.address?.postalCode,
    details?.address?.postOffice,
  ].filter((value): value is string => !!value);

  return parts.length > 0 ? parts.join(' ') : undefined;
};

const getDeviceEnrollmentValue = (unit: PlaceOrderItemUnitCoCo): string | undefined => {
  if (unit.deviceEnrollment === 'false') {
    return 'false';
  }
  return unit.enrollmentProgram ? (unit.deviceEnrollment ?? 'true') : undefined;
};

export const UNKNOWN_USER_PURPOSE_OF_USE = 'Käyttäjä ei tiedossa';
export const CHARACTERISTIC_VALUE_FROM_UNIT: Record<
  string,
  (unit: PlaceOrderItemUnitCoCo, contact?: Contact) => string | undefined
> = {
  [DeviceRuntimeCharacteristic.DEVICE_ENROLLMENT]: getDeviceEnrollmentValue,
  [DeviceRuntimeCharacteristic.ENROLLMENT_PROGRAM]: unit =>
    unit.deviceEnrollment === 'false' ? '' : unit.enrollmentProgram,
  [DeviceRuntimeCharacteristic.ENROLLMENT_CUSTOMER_ID]: unit =>
    unit.enrollmentProgram === EnrollmentProgramValue.APPLE ? unit.enrollmentCustomerId : undefined,
  [DeviceRuntimeCharacteristic.ENROLLMENT_ALIAS]: unit =>
    unit.enrollmentProgram === EnrollmentProgramValue.SAMSUNG ? unit.enrollmentAlias : undefined,
  [PlanProductRuntimeCharacteristic.MSISDN]: unit => unit.mobileNumber,
  [PlanProductRuntimeCharacteristic.PURPOSE_OF_USE]: (unit, contact) =>
    unit.purposeOfUse || (contact?.contactId ? undefined : UNKNOWN_USER_PURPOSE_OF_USE),
  [PlanProductRuntimeCharacteristic.USER_FIRST_NAME]: (_unit, contact) => contact?.person?.firstName,
  [PlanProductRuntimeCharacteristic.USER_LAST_NAME]: (_unit, contact) => contact?.person?.lastName,
  [PlanProductRuntimeCharacteristic.EMAIL_ID]: (_unit, contact) => contact?.person?.email,
  [PlanProductRuntimeCharacteristic.ORDERING_MOBILE_NUMBER]: (_unit, contact) => contact?.person?.phoneNumber,
  [PlanProductRuntimeCharacteristic.LANGUAGE]: (_unit, contact) => contact?.person?.preferredLanguage,
  [PlanProductRuntimeCharacteristic.DIRECTORY_LISTING_PUBLICITY]: getDirectoryListingPublicityValue,
};

const resolveCharacteristicValue = (
  c: ProductSpecRuntimeCharacteristic,
  unit: PlaceOrderItemUnitCoCo,
  contact: Contact | undefined,
  subcategory?: string
): string | undefined => {
  if (
    c.name === PlanProductRuntimeCharacteristic.DIRECTORY_LISTING_PUBLICITY &&
    isMobileBroadbandOffering(subcategory)
  ) {
    return NOT_TO_DIRECTORY_VALUE;
  }
  return CHARACTERISTIC_VALUE_FROM_UNIT[c.name]?.(unit, contact);
};

// An empty enrollmentProgram is sent on purpose: it tells the backend the device opted out of enrollment.
const shouldSendCharacteristicValue = (name: string, value: string | undefined): value is string =>
  !!value || (name === DeviceRuntimeCharacteristic.ENROLLMENT_PROGRAM && value === '');

const withPurposeOfUseCharacteristic = (
  characteristics: ProductSpecRuntimeCharacteristic[],
  subcategory?: string
): ProductSpecRuntimeCharacteristic[] =>
  getProductConfigDescriptor(subcategory)?.alwaysCollectPurposeOfUse &&
  !characteristics.some(c => c.name === PlanProductRuntimeCharacteristic.PURPOSE_OF_USE)
    ? [...characteristics, { name: PlanProductRuntimeCharacteristic.PURPOSE_OF_USE, type: 'String' }]
    : characteristics;

const mapOrderParamExtensionWithSelection = (unit: PlaceOrderItemUnitCoCo): Extensions[] => {
  const extensions: Extensions[] = [];
  if (unit.costCenter) {
    extensions.push({
      name: OrderParamExtensions.SUBSCRIPTION_COST_CENTER,
      target: 'orderItemParam',
      type: 'String',
      value: [{ value: unit.costCenter }],
    });
  }
  if (unit.subscriptionReference) {
    extensions.push({
      name: OrderParamExtensions.SUBSCRIPTION_REFERENCE,
      target: 'orderItemParam',
      type: 'String',
      value: [{ value: unit.subscriptionReference }],
    });
  }
  return extensions;
};
const mapPlanProductOfferingWithSelection = (
  basketItem: ProductOfferingCartItems,
  plan: CartProductOfferingCoco,
  unit: PlaceOrderItemUnitCoCo,
  selectedProductOffers: ProductOfferingsResponse[],
  productOfferId: string,
  contacts: Contact[]
): CartProductOfferingCoco => {
  const selectedOffer = selectedProductOffers.find(offer => offer.id === productOfferId);
  const contact = resolveUserContact(unit, contacts);

  if (!selectedOffer) {
    return {
      ...plan,
      productSpecRuntimeCharacteristics: plan.productSpecRuntimeCharacteristics ?? [],
      extensions: mapOrderParamExtensionWithSelection(unit),
    };
  }

  let runtimeCharacteristics = withPurposeOfUseCharacteristic(
    selectedOffer.planProductOffering?.productSpecRuntimeCharacteristics ??
      plan.productSpecRuntimeCharacteristics ??
      [],
    selectedOffer.productOfferingType?.subcategory
  );
  const offeringRuntimeCharacteristics =
    selectedOffer.planProductOffering?.productOfferingRuntimeCharacteristic ??
    plan.productOfferingRuntimeCharacteristic ??
    [];
  runtimeCharacteristics = [
    ...runtimeCharacteristics,
    ...offeringRuntimeCharacteristics
      .filter(characteristic => !runtimeCharacteristics.some(existing => existing.name === characteristic.name))
      .map(characteristic => ({ ...characteristic, type: characteristic.type ?? 'String' })),
  ];

  if (contact && isSoftwareProduct(selectedOffer)) {
    SOFTWARE_PRODUCT_CHAR_NAMES.forEach(name => {
      if (!runtimeCharacteristics.some(c => c.name === name)) {
        runtimeCharacteristics = [...runtimeCharacteristics, { name, type: 'String' }];
      }
    });
  }

  const configuredCharacteristics = runtimeCharacteristics.flatMap(c => {
    const value =
      c.name === PlanProductRuntimeCharacteristic.FIBER_MODE
        ? (getSelectedCharacteristicValue(basketItem.productOfferingRuntimeCharacteristic, c.name) ??
          getDefaultCharacteristicValue(runtimeCharacteristics, c.name))
        : resolveCharacteristicValue(c, unit, contact, selectedOffer.productOfferingType?.subcategory);
    return value ? [{ ...c, value: [{ value }] }] : [];
  });
  const mainUseOfSubscription = unit.additionalParameters?.[MAIN_USE_OF_SUBSCRIPTION];
  const productOfferingCharacteristics = mainUseOfSubscription
    ? (plan.productOfferingCharacteristic ?? []).map(characteristic =>
        characteristic.name === MAIN_USE_OF_SUBSCRIPTION
          ? { ...characteristic, value: [{ value: mainUseOfSubscription }] }
          : characteristic
      )
    : plan.productOfferingCharacteristic;

  return {
    ...plan,
    productOfferingCharacteristic: productOfferingCharacteristics,
    productSpecRuntimeCharacteristics: configuredCharacteristics,
    extensions: mapOrderParamExtensionWithSelection(unit),
  };
};

export const buildProductOrderRequest = (
  shoppingBasket: ShoppingBasketType,
  mdmId: string,
  loggedInUserMdmId: string
): ProductOrderRequest => ({
  cart: {
    cartId: shoppingBasket.cartId,
    created: shoppingBasket.created,
    productOfferings: shoppingBasket.productOfferings.map(mapCartItemToProductOffering),
    uiState: shoppingBasket.uiState,
  },
  relatedParty: [
    { referredType: 'Customer', id: mdmId, role: RelatedPartyRoles.CUSTOMER },
    { referredType: '', id: loggedInUserMdmId, role: RelatedPartyRoles.ASSIGNEE },
  ],
});

export const ADDON_CHARACTERISTIC_VALUE_FROM_UNIT: Record<
  string,
  (unit: PlaceOrderItemUnitCoCo) => string | undefined
> = {
  [BundleAddonGroupRuntimeCharacteristic.ICCID]: unit => unit.simCardNumber,
  [BundleAddonGroupRuntimeCharacteristic.OWN_SIM_CARD]: unit =>
    unit.simCardNumber ? OwnSimCardValue.YES : OwnSimCardValue.NO,
};

const hasCommercialRelation = (offering: CartProductOfferingCoco): boolean =>
  (offering.productOfferingRelationship ?? []).some(
    relationship => relationship.relationshipType === COMMERCIAL_RELATION_TYPE
  );

const pickOffering = (offering: CartProductOfferingCoco): CartProductOfferingCoco =>
  hasCommercialRelation(offering) ? pickCommercialAddonProductOffering(offering) : pickCartProductOffering(offering);

const isOwnSimCharacteristic = (characteristic: ProductSpecRuntimeCharacteristic): boolean =>
  characteristic.name === BundleAddonGroupRuntimeCharacteristic.OWN_SIM_CARD;

// The basket keeps no runtime characteristics for a directly bundled add-on, so its own-SIM declaration is taken from
// the catalogue, at the level where the catalogue declares it.
const withCatalogueOwnSimCharacteristic = (
  offering: CartProductOfferingCoco,
  catalogueAddon?: ProductOfferingCoco
): CartProductOfferingCoco => {
  const declaredByOffering = [
    ...(offering.productSpecRuntimeCharacteristics ?? []),
    ...(offering.productOfferingRuntimeCharacteristic ?? []),
  ].some(isOwnSimCharacteristic);
  const offeringLevel = catalogueAddon?.productOfferingRuntimeCharacteristic?.filter(isOwnSimCharacteristic) ?? [];
  const specLevel = catalogueAddon?.productSpecRuntimeCharacteristics?.filter(isOwnSimCharacteristic) ?? [];
  if (declaredByOffering || (offeringLevel.length === 0 && specLevel.length === 0)) {
    return offering;
  }

  return offeringLevel.length > 0
    ? {
        ...offering,
        productOfferingRuntimeCharacteristic: [
          ...(offering.productOfferingRuntimeCharacteristic ?? []),
          ...offeringLevel,
        ],
      }
    : {
        ...offering,
        productSpecRuntimeCharacteristics: [...(offering.productSpecRuntimeCharacteristics ?? []), ...specLevel],
      };
};

const mapAddonOfferingWithSelection = (
  offering: CartProductOfferingCoco,
  unit: PlaceOrderItemUnitCoCo,
  catalogueAddon?: ProductOfferingCoco
): CartProductOfferingCoco => {
  const mapped = withCatalogueOwnSimCharacteristic(pickOffering(offering), catalogueAddon);
  const declaresOwnSim = [
    ...(mapped.productSpecRuntimeCharacteristics ?? []),
    ...(mapped.productOfferingRuntimeCharacteristic ?? []),
  ].some(characteristic => characteristic.name === BundleAddonGroupRuntimeCharacteristic.OWN_SIM_CARD);
  // The API expects no ICCID at all when the SIM is not the customer's own.
  let characteristics = (mapped.productSpecRuntimeCharacteristics ?? []).filter(
    characteristic =>
      unit.simCardNumber || !declaresOwnSim || characteristic.name !== BundleAddonGroupRuntimeCharacteristic.ICCID
  );
  const selectedCharacteristics = unit.addonRuntimeCharacteristics?.[offering.id] ?? {};

  if (
    unit.simCardNumber &&
    isPhysicalSimOffering(offering) &&
    !characteristics.some(c => c.name === BundleAddonGroupRuntimeCharacteristic.ICCID)
  ) {
    characteristics = [...characteristics, { name: BundleAddonGroupRuntimeCharacteristic.ICCID, type: 'String' }];
  }

  Object.keys(selectedCharacteristics).forEach(name => {
    if (!characteristics.some(characteristic => characteristic.name === name)) {
      characteristics = [...characteristics, { name, type: 'String' }];
    }
  });

  const mapCharacteristicValue = <T extends ProductSpecRuntimeCharacteristic>(characteristic: T): T => {
    const value =
      selectedCharacteristics[characteristic.name] ?? ADDON_CHARACTERISTIC_VALUE_FROM_UNIT[characteristic.name]?.(unit);
    if (!value) {
      return characteristic;
    }
    const mappedValue =
      characteristic.name === BundleAddonGroupRuntimeCharacteristic.MAC_ADDRESS ? normalizeMacAddress(value) : value;
    return { ...characteristic, value: [{ value: mappedValue }] };
  };

  return {
    ...mapped,
    productSpecRuntimeCharacteristics: characteristics.map(mapCharacteristicValue),
    productOfferingRuntimeCharacteristic: mapped.productOfferingRuntimeCharacteristic?.map(mapCharacteristicValue),
  };
};

const mapBundleAddonGroupsWithSelection = (groups: CartBundleAddonGroup[], unit: PlaceOrderItemUnitCoCo) =>
  groups?.map(group => ({
    id: group.id,
    name: toNameField(group.name),
    productOffering: group.productOffering?.filter(o => o.isSelected).map(o => mapAddonOfferingWithSelection(o, unit)),
  }));

const mapUnitToProductOffering = (
  basketItem: ProductOfferingCartItems,
  unit: PlaceOrderItemUnitCoCo,
  selectedProductOffers: ProductOfferingsResponse[],
  contacts: Contact[]
): ProductOffering => {
  const mappedOffering = mapCartItemToProductOffering(basketItem);
  const contact = resolveUserContact(unit, contacts);

  const existingParties = (basketItem.relatedParty ?? []).filter(
    party => party.role !== RelatedPartyRoles.USER && party.role !== RelatedPartyRoles.INSTALLATION_CONTACT
  );

  const contactParty = contact?.contactId
    ? [
        {
          referredType: 'Individual',
          id: contact.contactId,
          role: RelatedPartyRoles.USER,
        },
      ]
    : [];
  const installationContactParty = unit.installationContact
    ? [
        {
          referredType: 'Individual',
          id: unit.installationContact,
          role: RelatedPartyRoles.INSTALLATION_CONTACT,
        },
      ]
    : [];

  const selectedOffer = selectedProductOffers.find(offer => offer.id === basketItem.id);
  const offeringLevelConfig = basketItem.planProductOffering
    ? {}
    : {
        productSpecRuntimeCharacteristics: withPurposeOfUseCharacteristic(
          selectedOffer?.productSpecRuntimeCharacteristics ?? [],
          basketItem.productOfferingType?.subcategory
        ).flatMap(c => {
          const value = resolveCharacteristicValue(c, unit, contact, basketItem.productOfferingType?.subcategory);
          return shouldSendCharacteristicValue(c.name, value) ? [{ ...c, value: [{ value }] }] : [];
        }),
        extensions: mapOrderParamExtensionWithSelection(unit),
      };

  return {
    ...mappedOffering,
    ...offeringLevelConfig,
    relatedParty: [...existingParties, ...contactParty, ...installationContactParty],
    planProductOffering:
      basketItem.planProductOffering &&
      mapPlanProductOfferingWithSelection(
        basketItem,
        basketItem.planProductOffering,
        unit,
        selectedProductOffers,
        basketItem.id,
        contacts
      ),
    bundleAddonProductOffering: (basketItem.bundledAddonProductOffering ?? [])
      .filter(addon => unit.productOfferAddons?.find(a => a.id === addon.id)?.isChecked)
      .map(addon =>
        mapAddonOfferingWithSelection(
          addon,
          unit,
          selectedOffer?.bundleAddonProductOffering?.find(catalogueAddon => catalogueAddon.id === addon.id)
        )
      ),
    commercialAddonProductOffering: (basketItem.commercialAddonProductOffering ?? [])
      .filter(addon => unit.productOfferAddons?.find(a => a.id === addon.id)?.isChecked)
      .map(addon => mapAddonOfferingWithSelection(addon, unit)),
    bundleAddonGroup: mapBundleAddonGroupsWithSelection(basketItem.bundleAddonGroup ?? [], unit),
  };
};

export interface BuildUpdateProductOrderOptions {
  /** Employee (EOE) orders do not collect purpose of use, so the characteristic is left out of the payload. */
  omitPurposeOfUse?: boolean;
}

const isNotPurposeOfUse = (c: ProductSpecRuntimeCharacteristic) =>
  c.name !== PlanProductRuntimeCharacteristic.PURPOSE_OF_USE;

const omitPurposeOfUse = (offering: ProductOffering): ProductOffering => ({
  ...offering,
  productSpecRuntimeCharacteristics: offering.productSpecRuntimeCharacteristics?.filter(isNotPurposeOfUse),
  planProductOffering: offering.planProductOffering && {
    ...offering.planProductOffering,
    productSpecRuntimeCharacteristics:
      offering.planProductOffering.productSpecRuntimeCharacteristics?.filter(isNotPurposeOfUse),
  },
});

export const buildUpdateProductOrderRequest = (
  shoppingBasket: ShoppingBasketType,
  placeOrderItemUnits: PlaceOrderItemUnitCoCo[],
  selectedProductOffers: ProductOfferingsResponse[],
  contacts: Contact[],
  installationAppointmentTimeSlotIds: Record<string, string> = {},
  options: BuildUpdateProductOrderOptions = {}
): UpdateProductOrderRequest => {
  const productOfferings: ProductOffering[] = shoppingBasket.productOfferings.flatMap(basketItem => {
    const unitsForItem = placeOrderItemUnits.filter(u => u.basketItemId === basketItem.cartItemId);
    return unitsForItem.map(unit => {
      const mappedOffering = mapUnitToProductOffering(basketItem, unit, selectedProductOffers, contacts);
      const productOffering: ProductOffering = {
        ...mappedOffering,
        extensions: mergeProductOfferingExtensions(
          basketItem,
          mappedOffering.extensions,
          installationAppointmentTimeSlotIds[basketItem.cartItemId],
          selectedProductOffers.find(offer => offer.id === basketItem.id)?.extensions
        ),
      };
      return options.omitPurposeOfUse ? omitPurposeOfUse(productOffering) : productOffering;
    });
  });

  return {
    cart: {
      cartId: shoppingBasket.cartId,
      created: shoppingBasket.created,
      productOfferings,
      uiState: shoppingBasket.uiState,
    },
  };
};

const DELIVERY_VALUE_FROM_ADDRESS: Record<string, (addr: CustomerPrimaryAddress) => string> = {
  [DeliveryRuntimeCharacteristic.ADDRESS_ID]: ({ addressId }) => addressId,
  [DeliveryRuntimeCharacteristic.RECIPIENT_NAME]: ({ deliveryAddress }) => deliveryAddress.recipient ?? '',
  [DeliveryRuntimeCharacteristic.PHONE_NUMBER]: ({ deliveryAddress }) => deliveryAddress.phoneNumber ?? '',
  [DeliveryRuntimeCharacteristic.ADDITIONAL_INFO]: ({ deliveryAddress }) => deliveryAddress.address?.line2 ?? '',
  [DeliveryRuntimeCharacteristic.STREET_ADDRESS]: ({ deliveryAddress }) => deliveryAddress.address?.line1 ?? '',
  [DeliveryRuntimeCharacteristic.POST_CODE]: ({ deliveryAddress }) => deliveryAddress.address?.postalCode ?? '',
  [DeliveryRuntimeCharacteristic.POST_OFFICE]: ({ deliveryAddress }) => deliveryAddress.address?.postOffice ?? '',
  [DeliveryRuntimeCharacteristic.COMPANY_NAME]: ({ deliveryAddress }) => deliveryAddress.companyName ?? '',
};
const DEFAULT_SELECTED_DELIVERY_OPTION = DeliveryOption.TOTAL_DELIVERY;

export const mapSelectedDeliveryOptionToOrderType = (
  selectedDeliveryOption: DeliveryOption = DEFAULT_SELECTED_DELIVERY_OPTION
): ShipmentOrderEnum =>
  selectedDeliveryOption === DeliveryOption.TOTAL_DELIVERY ? ShipmentOrderEnum.TOTAL : ShipmentOrderEnum.PARTIAL;

export const buildDeliveryMethodRequest = (
  selectedDeliveryMethod: ProductOfferingCoco,
  selectedDeliveryAddress: CustomerPrimaryAddress,
  selectedDeliveryOption: DeliveryOption = DEFAULT_SELECTED_DELIVERY_OPTION
): ProductOrderDeliveryMethodRequest => {
  const mappedExtensions = (selectedDeliveryMethod.extensions ?? []).map(c => {
    const valueResolver = DELIVERY_VALUE_FROM_ADDRESS[c.name];
    const matchesDeliveryTypeExtension = c.name === OrderParamExtensions.DELIVERY_TYPE;

    let extensionValue: string | undefined;
    if (matchesDeliveryTypeExtension) {
      extensionValue = mapSelectedDeliveryOptionToOrderType(selectedDeliveryOption);
    } else if (valueResolver) {
      extensionValue = valueResolver(selectedDeliveryAddress);
    }

    const mappedValue = extensionValue === undefined ? (c.value ?? [{ value: '' }]) : [{ value: extensionValue }];

    return {
      name: c.name,
      target: c.target ?? 'orderItemParam',
      type: c.type ?? 'String',
      ...(c.isMandatory !== undefined ? { isMandatory: c.isMandatory } : {}),
      value: mappedValue,
    };
  });

  return {
    selectedDeliveryOption,
    deliveryProductOffering: {
      id: selectedDeliveryMethod.id,
      cartItemId: 'cart-item-delivery-' + crypto.randomUUID(),
      name: getLocalizedString(selectedDeliveryMethod.name),
      productOfferingType: selectedDeliveryMethod.productOfferingType,
      productOfferingPrice: selectedDeliveryMethod.productOfferingPrice,
      extensions: mappedExtensions,
    },
  };
};
